import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type Draft = { id: string; name: string; content: string; stamp: string };

export function DraftEditor({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [edited, setEdited] = useState<Draft>();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const query = useQuery<Draft>({
    queryKey: ["governance-draft", id],
    queryFn: async () => (await apiRequest("GET", `/api/content-governance/${id}/draft`)).json(),
    refetchOnWindowFocus: false,
  });
  const draft = edited ?? query.data;
  async function save(submit: boolean) {
    if (!draft || locked.current) return;
    locked.current = true; setBusy(true); setError(""); setMessage("");
    try {
      const saved: Draft = await (await apiRequest("PUT", `/api/content-governance/${id}/draft`, draft)).json();
      setEdited(saved);
      if (submit) await apiRequest("POST", `/api/content-governance/${id}/submit`);
      await qc.invalidateQueries({ queryKey: ["content-governance"] });
      await qc.invalidateQueries({ queryKey: ["content-governance-summary"] });
      setMessage(submit ? "Submitted for review. Approval and publication are still required." : "Draft saved. Published content was not changed.");
      if (submit) {
        toast({ title: "Submitted for review", description: "Approval and publication are still required." });
        setEdited(undefined); qc.removeQueries({ queryKey: ["governance-draft", id] }); onClose();
      }
    } catch (e) { setError(e instanceof Error ? e.message : "Unable to save draft."); }
    finally { locked.current = false; setBusy(false); }
  }
  return <Dialog open onOpenChange={() => { if (!busy) onClose(); }}>
    <DialogContent className="max-h-[90dvh] overflow-y-auto">
      <DialogHeader><DialogTitle>Edit restored draft</DialogTitle>
        <DialogDescription>Editing this draft does not change published content. Submit it for normal review when ready.</DialogDescription>
      </DialogHeader>
      {query.isLoading && <p role="status">Loading draft…</p>}
      {query.error && <p role="alert">{query.error.message}</p>}
      {draft && <>
        <label htmlFor="restored-name">Name</label>
        <Input id="restored-name" maxLength={200} value={draft.name} disabled={busy} onChange={e => setEdited({ ...draft, name: e.target.value })} />
        <label htmlFor="restored-content">Content</label>
        <Textarea id="restored-content" rows={12} maxLength={200000} value={draft.content} disabled={busy} onChange={e => setEdited({ ...draft, content: e.target.value })} />
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => save(false)}>Save draft</Button>
          <Button disabled={busy} onClick={() => save(true)}>Save and submit for review</Button>
        </div>
      </>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      <Button variant="outline" disabled={busy} onClick={onClose}>Close</Button>
    </DialogContent>
  </Dialog>;
}
